import { type FC, useEffect, useState } from "react";

interface Props {
    id: string;
    content: string;
    application: string;
}

const ApplicationDOMRegistry = new Map<string, typeof HTMLElement>();

export const ApplicationDOMElement: FC<Props> = ({ id, content, application }) => {
    const [ element, setElement ] = useState<string | null>(null);

    useEffect(() => {
        if(ApplicationDOMRegistry.has(id)) {
            setElement(id);

            return;
        }

        const c = class extends HTMLElement {
            #shadow: ShadowRoot | undefined;
            #id!: string;

            protected handleScriptError = (e: ErrorEvent) => {
                if(e.error instanceof SyntaxError && e.error.stack?.includes("connectedCallback") && this.#shadow) {
                    this.#shadow.innerHTML = "<p>An error occurred while rendering this integrated content</p>";
                    window.removeEventListener("error", this.handleScriptError);
                }
            };

            public connectedCallback(): void {
                this.#shadow = this.attachShadow({ mode: "closed" });
                this.#shadow.innerHTML = content;

                const scripts = this.#shadow.querySelectorAll<HTMLScriptElement>("script");

                if(scripts.length) {
                    window.addEventListener("error", this.handleScriptError);
                    const waitForLoad = [];

                    for(let i = 0, l = scripts.length; i < l; i++) {
                        const parent = scripts[i].parentNode;

                        if(!parent) {
                            continue;
                        }

                        console.log(`Application ${application} is injecting a custom script into the UI`);
                        const newScript = document.createElement("script") as HTMLScriptElement;
                        const attributes = scripts[i].attributes;

                        for(let j = 0, n = attributes.length; j < n; j++) {
                            newScript.setAttribute(attributes[j].name, attributes[j].value);
                        }

                        if(scripts[i].innerText) {
                            if(typeof (window as any).shopfrontContexts === "undefined") {
                                (window as any).shopfrontContexts = {};
                            }

                            this.#id = id;
                            Object.defineProperty((window as any).shopfrontContexts, this.#id, {
                                enumerable  : false,
                                writable    : false,
                                value       : this.#shadow,
                                configurable: true,
                            });

                            newScript.appendChild(document.createTextNode(
                                `(() => { 
                                    const currentContext = window.shopfrontContexts["${this.#id}"]; 
                                    ${scripts[i].innerText}
                                 })();`
                            ));
                        }

                        if(newScript.hasAttribute("src")) {
                            waitForLoad.push(new Promise<void>(res => {
                                const event = () => {
                                    res();
                                    newScript.removeEventListener("load", event);
                                };

                                newScript.addEventListener("load", event);
                            }));
                        }

                        parent.replaceChild(newScript, scripts[i]);
                    }

                    Promise.all(waitForLoad)
                        .then(() => {
                            window.removeEventListener("error", this.handleScriptError);
                        });
                }
            }

            public disconnectedCallback(): void {
                if(typeof this.#id !== "undefined") {
                    if(typeof (window as any).shopfrontContexts?.[this.#id] !== "undefined") {
                        delete (window as any).shopfrontContexts[this.#id];
                    }
                }
            }
        };

        ApplicationDOMRegistry.set(id, c);
        customElements.define(id, c);
        setElement(id);
    }, [ id, content, application ]);

    if(!element) {
        return null;
    }

    const Element = element;

    return (
        <Element />
    );
};
